'use client';

import Link from 'next/link';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { PromoLabelItem } from '@/components/LabelsSection';
import { useFixedHeader } from '@/hooks/header';
import { useNavigation } from '@/hooks/navigation';
import { usePopupHeight } from '@/hooks/usePopupHeight';
import {
  BonanzaPromoDashboardContent,
  BonanzaPromoSnackbar,
  useBonanzaPromo,
  usePromotionsAvailability,
} from '@/modules/promotions';
import { useMissions } from '@/modules/promotions/mission/hooks/useMissions';
import { useGetCommonStore } from '@/store';
import { ScContainer } from '@/styles/common';
import { AuthButtons } from '../AuthButtons';
import { HeaderSkeleton } from '../HeaderSkeleton';
import { Logo } from '../Logo';
import { ScHeaderLogo, ScHeaderMobile, ScHeaderRowMob } from '../styled';
import { DepositPanel } from '../UserBlock';

export const HeaderMobile: FC = () => {
  const {
    i18n: { language },
  } = useTranslation('common');
  const isFixedHeader = useFixedHeader(language);

  const useCommonStore = useGetCommonStore();

  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const pendingUserPure = useCommonStore.use.pendingUserPure();

  const { generateLink } = useNavigation();

  const {
    isQuestAvailable,
    isBonanzaPromoAvailable,
    isFortuneWheelAvailable,
    isDiceRollAvailable,
    isScratchGameAvailable,
    isMissionAvailable,
  } = usePromotionsAvailability();
  const fortuneWheelMenuData = useCommonStore.use.fortuneWheelMenuData();
  const diceRollMenuData = useCommonStore.use.diceRollMenuData();
  const scratchGameMenuData = useCommonStore.use.scratchGameMenuData();
  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const { handleNavigateToBonanza } = useBonanzaPromo();

  const { popupHeight } = usePopupHeight();

  const { currentMission } = useMissions();

  return (
    <ScHeaderMobile
      $popupHeight={popupHeight}
      data-testid="mobile-header"
      isFixedHeader={isFixedHeader}
      wide={isAuthenticated}
      withBg
    >
      <ScContainer>
        {pendingUserPure ? (
          <HeaderSkeleton />
        ) : (
          <ScHeaderRowMob>
            <ScHeaderLogo>
              <Link data-testid="mobile-logo-link" href={generateLink('/')}>
                <Logo />
              </Link>
            </ScHeaderLogo>
            {isAuthenticated ? (
              <div className="panel" data-testid="mobile-user-panel">
                {isBonanzaPromoAvailable && !isMobileDevice && (
                  <>
                    <BonanzaPromoDashboardContent
                      onClick={handleNavigateToBonanza}
                    />
                    <BonanzaPromoSnackbar />
                  </>
                )}
                {isFortuneWheelAvailable && !!fortuneWheelMenuData?.menu && (
                  <PromoLabelItem
                    available
                    background={fortuneWheelMenuData?.menu?.bg?.url || ''}
                    character={fortuneWheelMenuData?.menu?.character?.url || ''}
                    url="/lucky-wheel"
                  />
                )}
                {isDiceRollAvailable && !!diceRollMenuData?.menu && (
                  <PromoLabelItem
                    available
                    background={diceRollMenuData?.menu?.bg?.url || ''}
                    character={diceRollMenuData?.menu?.character?.url || ''}
                    url="/dice-roll"
                  />
                )}
                {isQuestAvailable && (
                  <PromoLabelItem
                    available
                    background="/quest-xmas/menu-bg.webp"
                    character="/quest-xmas/menu-character.webp"
                    url="/quest"
                  />
                )}
                {isScratchGameAvailable && !!scratchGameMenuData?.menu && (
                  <PromoLabelItem
                    available
                    background={scratchGameMenuData?.menu?.bg?.url || ''}
                    url="/scratch-card"
                  />
                )}
                {isMissionAvailable && currentMission && (
                  <PromoLabelItem
                    available
                    background="/mission/menu-bg.webp"
                    character={currentMission.imgSrc}
                    url="/mission"
                  />
                )}
                <DepositPanel blackBg noPadding />
              </div>
            ) : (
              <div
                className={`reg-btns ${language.toLowerCase()}`}
                data-testid="mobile-auth-buttons"
              >
                <AuthButtons />
              </div>
            )}
          </ScHeaderRowMob>
        )}
      </ScContainer>
    </ScHeaderMobile>
  );
};
